import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Search, Check, X, ChevronRight } from "lucide-react";
import { SERVICE_CATEGORIES } from "@/lib/serviceCategories";

/**
 * Premium two-level category picker.
 * Props:
 *   open, onClose
 *   value: { category, subcategory, customLabel? }
 *   onChange: (value) => void
 *   categories?: array of { id, label, subs[] }  (defaults to SERVICE_CATEGORIES)
 *   title?: string used as the step-1 heading (e.g. "Choose a category")
 *   otherPlaceholder?: string for the free-text input under "Other"
 *   otherHelp?: helper line above the free-text input
 */
const CategoryPicker = ({
    open,
    onClose,
    value,
    onChange,
    categories = SERVICE_CATEGORIES,
    title = "Choose a category",
    otherPlaceholder = "Describe your service",
    otherHelp = "Tell us what kind of service you offer.",
}) => {
    const [step, setStep] = useState(value?.category ? "subs" : "cats");
    const [activeCat, setActiveCat] = useState(
        categories.find((c) => c.label === value?.category) || null
    );
    const [query, setQuery] = useState("");
    const [customText, setCustomText] = useState(value?.customLabel || "");

    useEffect(() => {
        if (open) {
            setQuery("");
            setStep(value?.category ? "subs" : "cats");
            setActiveCat(categories.find((c) => c.label === value?.category) || null);
            setCustomText(value?.customLabel || "");
        }
    }, [open, value, categories]);

    const filteredCats = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return categories;
        return categories.filter(
            (c) =>
                c.label.toLowerCase().includes(q) ||
                c.subs.some((s) => s.toLowerCase().includes(q))
        );
    }, [query, categories]);

    const filteredSubs = useMemo(() => {
        if (!activeCat) return [];
        const q = query.trim().toLowerCase();
        if (!q) return activeCat.subs;
        return activeCat.subs.filter((s) => s.toLowerCase().includes(q));
    }, [activeCat, query]);

    if (!open) return null;

    const pickCategory = (cat) => {
        setActiveCat(cat);
        setQuery("");
        if (cat.id === "other") {
            // Stay on Other input within this view
            setStep("other");
        } else {
            setStep("subs");
        }
    };

    const pickSub = (sub) => {
        onChange({ category: activeCat.label, subcategory: sub, customLabel: null });
        onClose();
    };

    const saveOther = () => {
        const txt = customText.trim();
        if (!txt) return;
        onChange({ category: "Other", subcategory: null, customLabel: txt });
        onClose();
    };

    return (
        <div
            className="absolute inset-0 z-40 flex items-end"
            data-testid="category-picker"
            role="dialog"
            aria-modal="true"
        >
            <button
                onClick={onClose}
                aria-label="Close"
                className="absolute inset-0 bg-black/45 animate-fade-in"
                data-testid="category-picker-scrim"
            />

            <div className="relative z-10 w-full bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.35)] animate-slide-up max-h-[85vh] flex flex-col">
                <div className="flex justify-center pt-2.5 pb-1 shrink-0">
                    <span className="h-1.5 w-10 rounded-full bg-muted" />
                </div>

                <div className="px-4 pt-1 pb-2 flex items-center gap-2 shrink-0">
                    {step === "subs" || step === "other" ? (
                        <button
                            data-testid="category-picker-back"
                            onClick={() => {
                                setStep("cats");
                                setActiveCat(null);
                                setQuery("");
                            }}
                            className="h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted"
                            aria-label="Back"
                        >
                            <ArrowLeft size={18} />
                        </button>
                    ) : (
                        <span className="h-9 w-9" />
                    )}
                    <h2 className="flex-1 text-center font-display text-[15px] font-semibold truncate">
                        {step === "cats"
                            ? title
                            : step === "other"
                            ? "Describe"
                            : activeCat?.label}
                    </h2>
                    <button
                        data-testid="category-picker-close"
                        onClick={onClose}
                        className="h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {step !== "other" && (
                    <div className="px-4 pb-2 shrink-0">
                        <label className="flex items-center h-11 px-3.5 rounded-full bg-secondary focus-within:bg-background focus-within:ring-2 focus-within:ring-primary/40">
                            <Search size={15} className="text-muted-foreground" />
                            <input
                                data-testid="category-picker-search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={step === "cats" ? "Search categories…" : "Search subcategories…"}
                                className="flex-1 ml-2 bg-transparent outline-none text-[13.5px]"
                                autoFocus
                            />
                        </label>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto no-scrollbar px-2 pb-[max(16px,env(safe-area-inset-bottom))]">
                    {step === "cats" && (
                        <ul className="py-1" data-testid="category-list">
                            {filteredCats.map((c) => {
                                const active = value?.category === c.label;
                                return (
                                    <li key={c.id}>
                                        <button
                                            data-testid={`category-row-${c.id}`}
                                            onClick={() => pickCategory(c)}
                                            className={[
                                                "w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-colors",
                                                active ? "bg-primary/5" : "hover:bg-accent/40",
                                            ].join(" ")}
                                        >
                                            <span className="flex-1 text-[14px] font-medium">{c.label}</span>
                                            {active && <Check size={16} className="text-primary" />}
                                            <ChevronRight size={16} className="text-muted-foreground" />
                                        </button>
                                    </li>
                                );
                            })}
                            {filteredCats.length === 0 && (
                                <li className="px-3 py-8 text-center text-[12.5px] text-muted-foreground">
                                    No matches.
                                </li>
                            )}
                        </ul>
                    )}

                    {step === "subs" && (
                        <ul className="py-1" data-testid="subcategory-list">
                            {filteredSubs.map((s) => {
                                const active = value?.subcategory === s && value?.category === activeCat.label;
                                return (
                                    <li key={s}>
                                        <button
                                            data-testid={`subcategory-row-${s}`}
                                            onClick={() => pickSub(s)}
                                            className={[
                                                "w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-colors",
                                                active ? "bg-primary/5" : "hover:bg-accent/40",
                                            ].join(" ")}
                                        >
                                            <span className="flex-1 text-[14px] font-medium">{s}</span>
                                            {active && <Check size={16} className="text-primary" />}
                                        </button>
                                    </li>
                                );
                            })}
                            {filteredSubs.length === 0 && (
                                <li className="px-3 py-8 text-center text-[12.5px] text-muted-foreground">
                                    No matches.
                                </li>
                            )}
                        </ul>
                    )}

                    {step === "other" && (
                        <div className="px-3 py-3" data-testid="category-picker-other">
                            <p className="text-[12.5px] text-muted-foreground mb-2">
                                {otherHelp}
                            </p>
                            <input
                                data-testid="category-picker-other-input"
                                value={customText}
                                onChange={(e) => setCustomText(e.target.value)}
                                placeholder={otherPlaceholder}
                                maxLength={60}
                                autoFocus
                                className="w-full h-12 px-4 rounded-2xl border border-border bg-secondary focus:bg-background focus:border-primary outline-none text-[14px]"
                            />
                            <button
                                data-testid="category-picker-other-save"
                                onClick={saveOther}
                                disabled={!customText.trim()}
                                className="mt-3 w-full h-12 rounded-2xl text-primary-foreground font-medium text-[14px] disabled:opacity-60 active:scale-[0.98]"
                                style={{
                                    background:
                                        "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                                }}
                            >
                                Save
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CategoryPicker;
